import { useHierarchyArea } from '@plarium/hierarchy-react';
import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { Alert, Grid, Section, makeStyles } from '@gogame/components';
import { withForm, FormField, SidePageV2, FormFieldGroup } from '@gogame/form';

import Period from 'scenes/Events/components/EventForm/components';
import {
  loadApplications,
  loadTriggers,
  loadTriggerParams,
} from 'scenes/Events/sources/fields';

import {
  APPLICATION,
  PROTOTYPE_ID,
  PARAMETERS,
  TRIGGER,
  DESCRIPTION,
  PURCHASE_DATA,
} from './constants';
import { eventSchema } from './schema';

const valdateField = (key) => (value) => {
  return !value ||
    value === undefined ||
    (Array.isArray(value) && isEmpty(value))
    ? `${key} is required`
    : undefined;
};
const useStyles = makeStyles({
  root: { paddingRight: '0 !important' },
});

const EventForm = withForm({
  enableReinitialize: true,
  validationSchema: eventSchema,
})(({
  values,
  onClose,
  title,
  subTitle,
  loading,
  onExited,
  open,
  entityId,
}) => {
  const classes = useStyles();
  const { data } = useHierarchyArea({
    name: 'trigger',
    source: loadTriggerParams,
    depends: [PROTOTYPE_ID],
    active: values[PROTOTYPE_ID] !== undefined,
    normalizeValue: (valueParams) => {
      return {
        ...valueParams,
        period: { length: null, unit: 'Day' },
        purchaseData: [1, 2],
      };
    },
  });

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'md'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section style={{ paddingTop: 8 }}>
          <Alert severity={'info'}>
            Event name will be generated automatically
          </Alert>
        </Section>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField.Select
                name={APPLICATION}
                label={'Application'}
                source={loadApplications}
                lazy={true}
                padding={'0'}
                required={true}
              />
            </Grid>
          </Grid>
        </Section>
        <Section title={'Event Trigger'}>
          <Grid container spacing={3}>
            <Grid item xs={6} className={classes.root}>
              <FormField.Select
                name={PROTOTYPE_ID}
                label={'Type'}
                lazy={true}
                required={true}
                padding={'0'}
                source={loadTriggers}
                active={values[APPLICATION] !== undefined}
                depends={[APPLICATION]}
                data-test-name={'EventTrigger'}
              />
            </Grid>
            <FormFieldGroup name={TRIGGER}>
              <Grid item xs={12} container spacing={3}>
                <FormFieldGroup name={PARAMETERS}>
                  {data?.parameters.map((item) => {
                    switch (item.input) {
                      case 'Number':
                        return (
                          <Grid
                            item
                            xs={3}
                            key={item.detailKey}
                            className={classes.root}
                          >
                            <FormField.TextField
                              type={'number'}
                              label={item.summary}
                              name={item.detailKey}
                              required={item.required}
                              validate={
                                item.required && valdateField(item.summary)
                              }
                              padding={'0'}
                            />
                          </Grid>
                        );
                      case 'Currency':
                        return (
                          <Grid
                            item
                            xs={3}
                            key={item.detailKey}
                            className={classes.root}
                          >
                            <FormField.TextField
                              type={'currency'}
                              label={item.summary}
                              name={item.detailKey}
                              required={item.required}
                              placeholder={'$0'}
                              validate={
                                item.required && valdateField(item.summary)
                              }
                              padding={'0'}
                            />
                          </Grid>
                        );
                      case 'Select':
                        return (
                          <Grid
                            item
                            xs={6}
                            key={item.detailKey}
                            className={classes.root}
                          >
                            <FormField.Select
                              label={item.summary}
                              name={item.detailKey}
                              options={item.options}
                              required={item.required}
                              validate={
                                item.required && valdateField(item.summary)
                              }
                              padding={'0'}
                            />
                          </Grid>
                        );

                      default:
                        return null;
                    }
                  })}
                </FormFieldGroup>
              </Grid>
              <Grid item xs={12} container spacing={3}>
                {data?.period ? (
                  <Grid item xs={6} className={classes.root}>
                    <Period {...data.period} />
                  </Grid>
                ) : null}

                {data?.purchaseData ? (
                  <Grid item xs={4}>
                    <FormField.CheckboxGroup
                      name={PURCHASE_DATA}
                      title={`${data.purchaseData.summary} *`}
                      required={data.purchaseData.required}
                      validate={
                        data.purchaseData.required &&
                        valdateField(data.purchaseData.summary)
                      }
                      checkboxVertical={true}
                      align={'center'}
                      padding={'0'}
                      options={data.purchaseData.options}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </FormFieldGroup>
          </Grid>
        </Section>
        <Section title={'Description'}>
          <FormField.TextField
            name={DESCRIPTION}
            label={'Description text'}
            padding={'0'}
            minRows={2}
            multiline
          />
        </Section>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default EventForm;
